import React from "react";
import Layout from "@/components/Layout";
import Field from "@/components/Field";
import AIButton from "@/components/AIButton";
import { DELIVERABLES_CHECKLIST, AI_ACTIONS, PILOT_RISKS } from "@/data/bridgeData";
import { DELIVERABLE_DOCS } from "@/data/activityTools";
import { useEntries } from "@/context/EntriesContext";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Circle, FileText } from "lucide-react";

const DOCS = DELIVERABLE_DOCS;

export default function Deliverables() {
  const { getEntry, saveEntry } = useEntries();
  const { user } = useAuth();
  const readOnly = false;
  const completed = DELIVERABLES_CHECKLIST.filter((_, i) => getEntry(`checklist:${i}`, false)).length;

  return (
    <Layout coachContext>
      <div className="label-eyebrow mb-3">Deliverables library</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-3">The work, made tangible</h1>
      <p className="max-w-2xl text-stone-600 leading-relaxed mb-10">
        Draft and refine the assets that carry your practice into the world. Use the assistant to sharpen language –
        the final word is always yours.
      </p>

      {/* Editable documents */}
      <div className="grid lg:grid-cols-2 gap-6 mb-14">
        {DOCS.map((d) => (
          <div key={d.key} className="rounded-sm border border-stone-200 bg-white p-7">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={15} strokeWidth={1.6} className="text-brand-primary" />
              <h3 className="font-serif text-2xl text-stone-900">{d.title}</h3>
            </div>
            <p className="text-sm text-stone-500 mb-4">{d.hint}</p>
            <Field entryKey={d.key} rows={5} testid={`doc-${d.key}`} placeholder="Draft freely, then refine…" />
            <AIButton
              task={(AI_ACTIONS[d.ai] || AI_ACTIONS.summarize).task}
              label={(AI_ACTIONS[d.ai] || AI_ACTIONS.summarize).label}
              testid={`doc-ai-${d.key}`}
              getInput={() => getEntry(d.key, "")}
              onResult={(out) => saveEntry(d.key, out, true)}
            />
          </div>
        ))}
      </div>

      {/* Full checklist */}
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-sm border border-stone-200 bg-white p-7">
          <div className="flex items-end justify-between mb-5">
            <div className="label-eyebrow">Full program checklist</div>
            <span className="text-sm text-stone-500">{completed}/{DELIVERABLES_CHECKLIST.length}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2.5">
            {DELIVERABLES_CHECKLIST.map((d, i) => {
              const key = `checklist:${i}`;
              const done = getEntry(key, false);
              return (
                <label key={d} data-testid={`checklist-${i}`} className="flex items-start gap-2.5 cursor-pointer">
                  <button onClick={() => saveEntry(key, !done, true)} className="mt-0.5 shrink-0">
                    {done ? <CheckCircle2 size={17} className="text-brand-sage" /> : <Circle size={17} className="text-stone-300" />}
                  </button>
                  <span className={`text-sm leading-snug ${done ? "text-stone-400 line-through" : "text-stone-700"}`}>{d}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-7">
          <div className="label-eyebrow mb-4">Watch-fors</div>
          <ul className="space-y-4">
            {PILOT_RISKS.slice(0, 4).map((r) => (
              <li key={r.title}>
                <div className="text-sm font-medium text-stone-900 leading-snug">{r.title}</div>
                <div className="text-xs text-stone-500 mt-0.5">{r.response}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Layout>
  );
}
